"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, type ReactNode } from "react";
import { Package, Leaf, FlaskRound, Coffee, Droplets } from "lucide-react";
import { useLocale } from "@/lib/useLocale";
import { t } from "@/lib/translations";

const categoryIcons: Record<string, React.ReactNode> = {
  Powder: <Droplets className="w-4 h-4" />,
  "Fresh Herbs": <Leaf className="w-4 h-4" />,
  Extracts: <FlaskRound className="w-4 h-4" />,
  Beans: <Coffee className="w-4 h-4" />,
  Leaves: <Leaf className="w-4 h-4" />,
};

const categoryColors: Record<string, string> = {
  Powder: "bg-cyan-50 text-cyan-700 border-cyan-200",
  "Fresh Herbs": "bg-brand-50 text-brand-700 border-brand-200",
  Extracts: "bg-purple-50 text-purple-700 border-purple-200",
  Beans: "bg-amber-50 text-amber-700 border-amber-200",
  Leaves: "bg-teal-50 text-teal-700 border-teal-200",
};

interface ProductCardProps {
  id: string;
  slug: string;
  title: string;
  shortDesc: string;
  category: string;
  type: string;
  images: string[];
  active: boolean;
  moq: number;
  certifications: string[];
  layout?: "grid" | "list";
}

export default function ProductCard({
  id,
  slug,
  title,
  shortDesc,
  category,
  type,
  images,
  active,
  moq,
  certifications,
  layout = "grid",
}: ProductCardProps) {
  const [imgError, setImgError] = useState(false);
  const { locale } = useLocale();
  const hasImage = images?.[0] && !imgError;
  const placeholderDataUri = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%23f1f5f9'/%3E%3Ctext x='200' y='155' text-anchor='middle' fill='%2394a3b8' font-family='sans-serif' font-size='16'%3ENo Image%3C/text%3E%3C/svg%3E";

  if (layout === "list") {
    return (
      <div className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-brand-200 flex flex-col sm:flex-row">
        <Link href={`/products/${slug}`} className="sm:w-56 shrink-0">
          <div className="relative h-48 sm:h-40 w-full bg-gray-200 overflow-hidden">
            {active && hasImage ? (
              <Image
                src={images[0]}
                alt={title}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-500"
                sizes="256px"
                onError={() => setImgError(true)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <img src={placeholderDataUri} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="absolute top-2 left-2 flex flex-wrap gap-1.5">
              <span className="text-[10px] font-medium bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full shadow-sm flex items-center gap-1">
                <Package className="w-2.5 h-2.5 text-gray-500" />
                {t(locale, "detail.moq")} {moq} kg
              </span>
            </div>
          </div>
        </Link>
        <div className="flex-1 p-4 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span
                className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border flex items-center gap-1 ${
                  categoryColors[category] || "bg-gray-50 text-gray-700 border-gray-200"
                }`}
              >
                {categoryIcons[category] || <Package className="w-3 h-3" />}
                {category}
              </span>
              <span className="text-[10px] text-gray-400 uppercase tracking-wide">{type}</span>
            </div>
            <Link href={`/products/${slug}`}>
              <h3 className="text-base font-semibold font-display text-gray-900 hover:text-brand-600 transition-colors leading-snug">
                {title}
              </h3>
            </Link>
            <p className="text-sm text-gray-500 mt-1 line-clamp-2 leading-relaxed">
              {shortDesc}
            </p>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
            <Link
              href={`/products/${slug}`}
              className="text-sm font-medium text-brand-600 hover:text-brand-700 transition-colors"
            >
              {t(locale, "card.viewDetails")} &rarr;
            </Link>
            <Link
              href={`/export-inquiry?product=${id}`}
              className="text-sm font-medium text-brand-600 hover:text-brand-700 transition-colors bg-brand-50 hover:bg-brand-100 px-3 py-1.5 rounded-lg"
            >
              {t(locale, "card.requestQuote")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-brand-200">
      <Link href={`/products/${slug}`}>
        <div className="relative h-56 sm:h-64 w-full bg-gray-200 overflow-hidden">
          {active && hasImage ? (
            <Image
              src={images[0]}
              alt={title}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-500"
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <img src={placeholderDataUri} alt="" className="w-full h-full object-cover" />
            </div>
          )}

          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wider bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full shadow-sm flex items-center gap-1.5">
              <Package className="w-3 h-3 text-gray-500" />
              {t(locale, "detail.moq")} {moq} kg
            </span>
          </div>

          <div className="absolute top-3 right-3 flex flex-wrap gap-1.5 justify-end">
            {certifications.slice(0, 2).map((cert) => (
              <span
                key={cert}
                className="text-[10px] font-medium bg-brand-50/90 backdrop-blur-sm text-brand-700 px-2 py-0.5 rounded-full border border-brand-200/50"
              >
                {cert}
              </span>
            ))}
            {certifications.length > 2 && (
              <span className="text-[10px] font-medium bg-gray-50/90 backdrop-blur-sm text-gray-500 px-2 py-0.5 rounded-full">
                +{certifications.length - 2}
              </span>
            )}
          </div>
        </div>
      </Link>

      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span
            className={`text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border flex items-center gap-1 ${
              categoryColors[category] || "bg-gray-50 text-gray-700 border-gray-200"
            }`}
          >
            {categoryIcons[category] || <Package className="w-3.5 h-3.5" />}
            {category}
          </span>
          <span className="text-xs text-gray-400 uppercase tracking-wide">{type}</span>
        </div>

        <Link href={`/products/${slug}`}>
          <h3 className="text-base font-semibold font-display text-gray-900 hover:text-brand-600 transition-colors leading-snug">
            {title}
          </h3>
        </Link>

        <p className="text-sm text-gray-500 mt-1.5 line-clamp-2 leading-relaxed">
          {shortDesc}
        </p>

        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
          <Link
            href={`/products/${slug}`}
            className="text-sm font-medium text-brand-600 hover:text-brand-700 transition-colors"
          >
            {t(locale, "card.viewDetails")} &rarr;
          </Link>

          <Link
            href={`/export-inquiry?product=${id}`}
            className="text-sm font-medium text-brand-600 hover:text-brand-700 transition-colors bg-brand-50 hover:bg-brand-100 px-3 py-1.5 rounded-lg"
          >
            {t(locale, "card.requestQuote")}
          </Link>
        </div>
      </div>
    </div>
  );
}
